/**
 * FERP Portal - Form Schema Override List Page
 *
 * Config type fso-: Tenant-specific overrides on base form schemas. Overrides can
 * hide fields, make fields read-only, relabel fields, add custom fields, and add
 * custom validation rules. Two-layer storage: base in FormSchema, overrides in
 * tenant-scoped DDB records.
 *
 * Architecture:
 *   - Backend: formSchemaOverride query + saveFormSchemaOverride/deleteFormSchemaOverride mutations
 *   - Repository: FormSchemaRepository (two-layer: base + override)
 *   - Config source: common/configs/seed/form-schema-overrides/fso-*.yaml
 *   - DRR-055/DRR-074: Metadata-driven frontend enforcement
 *
 * CONSTRAINTS:
 * - Mantine v8.x only
 * - Dumb Frontend: all data from backend
 * - Architect view layer only (overrides are tenant configuration)
 */

import { useState, useMemo, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  useFormSchemasQuery,
  useGetFormSchemaOverrideQuery,
  useEditFormSchemaMutation,
  type FormSchemasQuery,
  type FormSafetyLevel,
} from '@/generated/graphql';
import {
  Alert,
  Badge,
  Card,
  Container,
  Group,
  Loader,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  ThemeIcon,
  Title,
} from '@mantine/core';
import {
  IconAdjustments,
  IconAlertTriangle,
  IconChevronRight,
  IconInfoCircle,
  IconLayersSubtract,
  IconSearch,
} from '@tabler/icons-react';

const SUBGRAPHS = ['core', 'fhir', 'erp', 'payroll'] as const;

const SUBGRAPH_COLORS: Record<string, string> = {
  core: 'blue',
  fhir: 'cyan',
  erp: 'orange',
  payroll: 'teal',
};

const SAFETY_COLORS: Record<string, string> = {
  CRITICAL: 'red',
  HIGH: 'orange',
  MEDIUM: 'yellow',
  LOW: 'green',
  NONE: 'gray',
};

type SchemaEntry = FormSchemasQuery['formSchemas'][number] & { subgraph: string };

export function FormSchemaOverrideListPage() {
  const [search, setSearch] = useState('');
  const [selectedSchema, setSelectedSchema] = useState<{ subgraph: string; entityType: string } | null>(null);

  // Query all 4 subgraphs for their form schemas
  const coreQuery = useFormSchemasQuery({ variables: { subgraph: 'core' } });
  const fhirQuery = useFormSchemasQuery({ variables: { subgraph: 'fhir' } });
  const erpQuery = useFormSchemasQuery({ variables: { subgraph: 'erp' } });
  const payrollQuery = useFormSchemasQuery({ variables: { subgraph: 'payroll' } });

  // DRR-197/§46: Feature surfacing — FormSchemaOverride query for selected schema
  const { data: overrideData, loading: overrideLoading } = useGetFormSchemaOverrideQuery({
    variables: {
      subgraph: selectedSchema?.subgraph ?? '',
      entityType: selectedSchema?.entityType ?? '',
    },
    skip: !selectedSchema,
  });

  // DRR-197/§46: Feature surfacing — EditFormSchema mutation (AI-assisted editing)
  const [editFormSchema, { loading: editLoading }] = useEditFormSchemaMutation();

  // Handler for AI-assisted form schema editing (available for UI wiring)
  const handleEditWithAI = useCallback(async (prompt: string) => {
    if (!selectedSchema) return;
    try {
      const result = await editFormSchema({
        variables: {
          input: {
            prompt,
            subgraph: selectedSchema.subgraph,
            entityType: selectedSchema.entityType,
          },
        },
      });
      // eslint-disable-next-line no-console
      console.log('AI Edit Response:', result.data?.editFormSchema.response);
      return result.data?.editFormSchema;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('AI Edit failed:', err);
    }
  }, [editFormSchema, selectedSchema]);

  // Mark these as available for future UI wiring
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _featureSurfacing = { overrideData, overrideLoading, handleEditWithAI, editLoading, setSelectedSchema };

  const loading =
    coreQuery.loading || fhirQuery.loading || erpQuery.loading || payrollQuery.loading;
  const error = coreQuery.error || fhirQuery.error || erpQuery.error || payrollQuery.error;

  const allSchemas = useMemo(() => {
    const schemas: SchemaEntry[] = [];
    for (const [subgraph, query] of [
      ['core', coreQuery],
      ['fhir', fhirQuery],
      ['erp', erpQuery],
      ['payroll', payrollQuery],
    ] as const) {
      for (const s of query.data?.formSchemas ?? []) {
        schemas.push({ ...s, subgraph });
      }
    }
    return schemas;
  }, [coreQuery.data, fhirQuery.data, erpQuery.data, payrollQuery.data]);

  const filtered = useMemo(() => {
    if (!search) return allSchemas;
    const lower = search.toLowerCase();
    return allSchemas.filter(
      (s) =>
        s.entityType.toLowerCase().includes(lower) ||
        s.displayName.toLowerCase().includes(lower) ||
        s.subgraph.toLowerCase().includes(lower),
    );
  }, [allSchemas, search]);

  const subgraphCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const s of allSchemas) {
      counts[s.subgraph] = (counts[s.subgraph] ?? 0) + 1;
    }
    return counts;
  }, [allSchemas]);

  if (error) {
    return (
      <Container size="lg" py="xl">
        <Alert icon={<IconAlertTriangle size={16} />} color="red" title="Error Loading Schemas">
          {error.message}
        </Alert>
      </Container>
    );
  }

  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        <Group justify="space-between">
          <div>
            <Title order={2}>Form Schema Overrides</Title>
            <Text c="dimmed" size="sm">
              Config type fso- — tenant-specific overrides on base form schemas (DRR-055, DRR-074,
              DRR-160)
            </Text>
          </div>
          <Badge variant="light" color="indigo" size="lg">
            {allSchemas.length} Base Schemas
          </Badge>
        </Group>

        <Alert icon={<IconInfoCircle size={16} />} color="blue" title="Two-Layer Architecture">
          Base form schemas define the universal field structure for each entity type. Overrides add
          tenant-specific customization: hide fields, relabel, add custom fields, add validation
          rules. The FormRenderer merges base + override at query time. Full CRUD via
          saveFormSchemaOverride / deleteFormSchemaOverride mutations.
        </Alert>

        <TextInput
          placeholder="Search by entity type, display name, or subgraph..."
          leftSection={<IconSearch size={16} />}
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
        />

        <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="md">
          {Object.entries(subgraphCounts).map(([subgraph, count]) => (
            <Card key={subgraph} withBorder p="sm">
              <Group gap="sm">
                <Badge
                  variant="light"
                  color={SUBGRAPH_COLORS[subgraph] ?? 'gray'}
                  size="sm"
                >
                  {subgraph}
                </Badge>
                <Text size="sm" fw={500}>
                  {count} schemas
                </Text>
              </Group>
            </Card>
          ))}
        </SimpleGrid>

        <Card withBorder p="md">
          <Group gap="sm" mb="xs">
            <ThemeIcon variant="light" color="indigo">
              <IconLayersSubtract size={18} />
            </ThemeIcon>
            <Text fw={600}>How Overrides Work</Text>
          </Group>
          <Text size="sm" c="dimmed">
            Base FormSchema (DDB) + Tenant Override (DDB, Valkey 1min cache) →{' '}
            <Text span ff="monospace" size="sm">
              formSchema(subgraph, entityType)
            </Text>{' '}
            query merges both →{' '}
            <Text span ff="monospace" size="sm">
              useFormSchema()
            </Text>{' '}
            → FormRenderer. Override operations: hiddenFields (remove), readonlyFields (lock),
            labelOverrides (rename), customFields (append), customValidations (add rules).
          </Text>
        </Card>

        {loading && (
          <Group justify="center" py="xl">
            <Loader />
          </Group>
        )}

        <Stack gap="sm">
          {filtered.map((schema) => (
            <Card
              key={`${schema.subgraph}-${schema.entityType}`}
              withBorder
              p="sm"
              component={Link}
              to={`/configure/form-overrides/${schema.subgraph}/${schema.entityType}`}
              style={{ textDecoration: 'none', cursor: 'pointer' }}
            >
              <Group justify="space-between">
                <Group gap="sm">
                  <ThemeIcon
                    variant="light"
                    color={SUBGRAPH_COLORS[schema.subgraph] ?? 'gray'}
                    size="sm"
                  >
                    <IconAdjustments size={14} />
                  </ThemeIcon>
                  <div>
                    <Text size="sm" fw={500}>
                      {schema.displayName}
                    </Text>
                    <Text size="xs" c="dimmed" ff="monospace">
                      {schema.subgraph}:{schema.entityType}
                    </Text>
                  </div>
                </Group>
                <Group gap="xs">
                  <Badge
                    variant="light"
                    color={SUBGRAPH_COLORS[schema.subgraph] ?? 'gray'}
                    size="xs"
                  >
                    {schema.subgraph}
                  </Badge>
                  <Badge
                    variant="outline"
                    size="xs"
                    color={SAFETY_COLORS[schema.safetyLevel] ?? 'gray'}
                  >
                    {schema.safetyLevel}
                  </Badge>
                  <Badge variant="outline" size="xs" color="gray">
                    v{schema.version}
                  </Badge>
                  <IconChevronRight size={16} color="gray" />
                </Group>
              </Group>
              {schema.description && (
                <Text size="xs" c="dimmed" mt={4}>
                  {schema.description}
                </Text>
              )}
            </Card>
          ))}
        </Stack>

        {!loading && filtered.length === 0 && (
          <Text c="dimmed" ta="center" py="xl">
            {search
              ? 'No schemas match your search.'
              : 'No form schemas configured for this tenant.'}
          </Text>
        )}
      </Stack>
    </Container>
  );
}

export default FormSchemaOverrideListPage;
